import { IPublicClientApplication } from '@azure/msal-browser';
import { graphGet, graphPutJson } from '../api/graphClient';
import { getGraphHeaders } from '../auth/authService';

const GRAPH = 'https://graph.microsoft.com/v1.0';

export interface M365Group {
  id: string;
  displayName: string;
  mail: string | null;
}

export interface GroupMember {
  id: string;
  displayName: string;
  mail: string | null;
  userPrincipalName: string | null;
}

/** Search Unified (M365) groups by display name. Requires ConsistencyLevel: eventual. */
export async function searchGroups(pca: IPublicClientApplication, term: string): Promise<M365Group[]> {
  const filter = encodeURIComponent("groupTypes/any(c:c eq 'Unified')");
  const search = encodeURIComponent(`"displayName:${term}"`);
  const data = await graphGet<{ value: M365Group[] }>(
    pca,
    `/groups?$filter=${filter}&$search=${search}&$select=id,displayName,mail&$top=15`,
    { ConsistencyLevel: 'eventual' },
  );
  return data.value ?? [];
}

export async function getGroup(pca: IPublicClientApplication, groupId: string): Promise<M365Group> {
  return graphGet<M365Group>(pca, `/groups/${groupId}?$select=id,displayName,mail`);
}

/** Group members (user roster) — primary @mention source when a Group is linked. */
export async function getGroupMembers(pca: IPublicClientApplication, groupId: string): Promise<GroupMember[]> {
  const data = await graphGet<{ value: GroupMember[] }>(
    pca, `/groups/${groupId}/members?$select=id,displayName,mail,userPrincipalName&$top=100`,
  );
  return (data.value ?? []).filter(m => m.id && m.displayName);
}

export function slugify(name: string): string {
  const base = name.toLowerCase().replace(/[^a-z0-9]+/g, '').slice(0, 40) || 'project';
  // Suffix keeps the mailNickname unique within the tenant.
  return `${base}${Date.now().toString(36)}`.slice(0, 60);
}

const sleep = (ms: number) => new Promise(r => setTimeout(r, ms));

/**
 * Provision an M365 Group then add Teams capability, per the locked
 * architecture: POST /groups → poll until the group resolves → PUT
 * /groups/{id}/team. NEVER POST /teams directly. Returns the new group id.
 * Graph error detail is surfaced on failure.
 */
export async function createGroupWithTeam(
  pca: IPublicClientApplication, displayName: string,
): Promise<string> {
  const headers = await getGraphHeaders(pca);
  const createRes = await fetch(`${GRAPH}/groups`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      displayName,
      mailEnabled: true,
      securityEnabled: false,
      groupTypes: ['Unified'],
      mailNickname: slugify(displayName),
    }),
  });
  if (!createRes.ok) {
    const text = await createRes.text().catch(() => '');
    throw new Error(`Create group failed (${createRes.status}): ${text.slice(0, 400)}`);
  }
  const group = await createRes.json() as { id: string };
  const groupId = group.id;

  // Poll until the group is readable (replication) — up to ~10s.
  const deadline = Date.now() + 10_000;
  while (Date.now() < deadline) {
    try { await getGroup(pca, groupId); break; }
    catch { await sleep(1500); }
  }

  // Add Teams capability via PUT /groups/{id}/team (Group-first, never POST /teams).
  await graphPutJson(pca, `/groups/${groupId}/team`, {
    'memberSettings': { 'allowCreateUpdateChannels': true },
    'messagingSettings': { 'allowUserEditMessages': true, 'allowUserDeleteMessages': true },
  });

  return groupId;
}
